import { inject, Injectable } from '@angular/core';
import { UserState, UserStateModel } from '@celerofinancas/state-management';
import { Store } from '@ngxs/store';
import { distinctUntilChanged, filter, map, Observable } from 'rxjs';

/**
 * Single source of truth for whether the logged user accepted the terms of use.
 *
 * The acceptance belongs to the user, not to any screen or component: it is
 * `privacy_policy_acceptance_date`, returned by `GET /v2/me/read` — the field
 * the backend fills when the user confirms the terms in the initial journey
 * modal. Anything that must stay hidden until then reads it from here, so a
 * single predicate answers the question for the whole application and no
 * feature can drift into its own interpretation of "accepted".
 */
@Injectable({
  providedIn: 'root',
})
export class PolicyAcceptanceService {
  /**
   * Store service
   */
  private readonly store = inject(Store);

  /**
   * Whether the logged user accepted the terms of use, as it changes.
   *
   * Emits the current answer right away and again whenever it flips — so a
   * user who accepts mid-session is seen as accepted from that point on.
   */
  public readonly hasAccepted$: Observable<boolean> = this.store
    .select(UserState.getState)
    .pipe(
      map((user: UserStateModel) => this.isAccepted(user)),
      distinctUntilChanged(),
    );

  /**
   * Whether the logged user accepted the terms of use, emitted only once the
   * user is loaded.
   *
   * `UserState` starts empty and is filled by the `me/read` call in the
   * application bootstrap. Reading it before that answers "not accepted" for
   * a user who did accept, so whoever decides something once — and drops it
   * when the answer is no — must wait for the user to resolve instead of
   * racing the bootstrap.
   */
  public readonly hasAcceptedOnceUserLoaded$: Observable<boolean> = this.store
    .select(UserState.getState)
    .pipe(
      filter((user: UserStateModel) => !!user?.pk),
      map((user: UserStateModel) => this.isAccepted(user)),
    );

  /**
   * Whether a user accepted the terms of use.
   *
   * @param user - user as stored in `UserState`
   */
  private isAccepted(user: UserStateModel): boolean {
    return !!user?.privacy_policy_acceptance_date;
  }
}
